import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { postJournal } from '../../api/journal';
import { ACCOUNTS } from '../../api/accounts';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import { getInvoicePaidAmount, computeFifoAllocation } from '../../api/receiptAllocations';

function fmt(n) { return parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }); }
function daysSince(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

export default function SuspenseRegister() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';

  const [tab, setTab] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reclassifying, setReclassifying] = useState(null); // the receipt_voucher row being reclassified
  const [clientLabel, setClientLabel] = useState('');
  const [clientId, setClientId] = useState('');
  const [target, setTarget] = useState('deposit'); // 'deposit' | 'invoice' | 'fifo'
  const [openInvoices, setOpenInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [fifoAllocations, setFifoAllocations] = useState([]); // FIFO split of the suspense amount: old balance -> invoices oldest-first -> advance
  const [fifoLoading, setFifoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [reclassifyBranchId, setReclassifyBranchId] = useState('');

  // Split mode — the agent deposited one lump sum that's actually several different
  // customers' payments bundled together. Instead of one client/target for the whole
  // amount, staff break it into per-customer portions, each with its own target
  // (general deposit / FIFO against balance / a specific invoice).
  const [splitMode, setSplitMode] = useState(false);

  // Internal transfer — the "unidentified" deposit was our own money moved from another of
  // our bank/cash accounts, not a customer payment. Cleared against the source account
  // (DR Suspense / CR source bank) so it nets to a plain bank-to-bank transfer, with no
  // client, AR or revenue involved. If the outgoing side was already booked elsewhere, the
  // suspense receipt double-counted it, so it's cleared against the receiving account instead.
  const [clearMode, setClearMode] = useState('client'); // 'client' | 'transfer'
  const [transferFromId, setTransferFromId] = useState('');
  // Where the money actually landed. Defaults to the account the suspense receipt was
  // recorded into, but can be corrected if it was logged against the wrong bank.
  const [transferIntoId, setTransferIntoId] = useState('');
  const [outflowAlreadyRecorded, setOutflowAlreadyRecorded] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [splits, setSplits] = useState([]); // [{key, clientId, clientLabel, amount, target, openInvoices, selectedInvoiceId, fifoAllocations, fifoLoading}]
  const splitKeySeq = React.useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('receipt_vouchers')
      .select('id, voucher_number, voucher_date, amount, currency_id, bank_reference, description, received_into_account_id, suspense_cleared_at, suspense_cleared_by, client_id, branch_id, currencies:currency_id(code), received_into:received_into_account_id(account_code, account_name), branches:branch_id(name)')
      .eq('is_suspense', true)
      .order('voucher_date', { ascending: false });
    q = tab === 'open' ? q.is('suspense_cleared_journal_id', null) : q.not('suspense_cleared_journal_id', 'is', null);
    const { data } = await q;
    setRows(data || []);
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name')
      .then(({ data }) => setBranches(data || []));
    // Bank/cash accounts only (below 1030) — the same set a Payment Voucher can pay from.
    supabase.from('chart_of_accounts').select('id, account_code, account_name').eq('is_active', true).eq('is_payment_source', true)
      .lt('account_code', '1030').order('account_code')
      .then(({ data }) => setBankAccounts(data || []));
  }, []);

  const totalOpen = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  const openReclassify = (row) => {
    setReclassifying(row);
    setClientLabel(''); setClientId(''); setTarget('deposit');
    setOpenInvoices([]); setSelectedInvoiceId(''); setFifoAllocations([]);
    setSplitMode(false); setSplits([]);
    setClearMode('client'); setTransferFromId(''); setOutflowAlreadyRecorded(false);
    setTransferIntoId(row.received_into_account_id || '');
    // The branch is rarely known at deposit time (that's the whole reason it landed in
    // Suspense) — default to whatever branch it was logged under, if any, but let staff
    // correct it now that the depositor (and so the branch) is known.
    setReclassifyBranchId(row.branch_id || '');
    setError('');
  };

  const loadClientInvoices = async (id) => {
    const { data } = await supabase.from('invoices')
      .select('id, invoice_number, issued_date, status, line_items')
      .eq('client_id', id).in('status', ['DRAFT', 'SENT']).order('issued_date', { ascending: false }).limit(20);
    const withBalances = (await Promise.all((data || []).map(async inv => {
      const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price || li.unitPrice || 0) * parseFloat(li.qty || li.quantity || 1)), 0);
      const paid = await getInvoicePaidAmount(inv.id);
      return { ...inv, total, paid, balance: total - paid };
    }))).filter(i => i.balance > 0.01);
    setOpenInvoices(withBalances);
  };

  // FIFO preview: how this suspense amount would split — oldest opening balance first,
  // then open invoices oldest-issued first, any leftover falls through as an unapplied advance.
  const loadFifoPreview = async (id, amount) => {
    setFifoLoading(true);
    try {
      const allocs = await computeFifoAllocation(id, parseFloat(amount) || 0);
      setFifoAllocations(allocs);
    } finally {
      setFifoLoading(false);
    }
  };

  const hasOutstanding = fifoAllocations.some(a => a.target_type !== 'advance');

  const submitTransfer = async () => {
    if (!reclassifyBranchId) return setError('Select which branch this deposit belongs to.');
    if (!outflowAlreadyRecorded && !transferFromId) return setError('Pick the account the money was transferred from.');
    if (!transferIntoId) return setError('Pick the account the money was received into.');
    if (!outflowAlreadyRecorded && transferFromId === transferIntoId) {
      return setError('The source account must be different from the account it was received into.');
    }
    setSaving(true); setError('');
    try {
      const amt = parseFloat(reclassifying.amount);
      const from = bankAccounts.find(a => a.id === transferFromId);
      const fromLabel = from ? `${from.account_code} ${from.account_name}` : '';
      const into = bankAccounts.find(a => a.id === transferIntoId);
      const intoLabel = into ? `${into.account_code} ${into.account_name}` : 'receiving account';
      const origInto = reclassifying.received_into_account_id;
      const intoChanged = !!origInto && transferIntoId !== origInto;
      const creditAccount = outflowAlreadyRecorded ? transferIntoId : transferFromId;
      const desc = outflowAlreadyRecorded
        ? `Suspense cleared — internal transfer already recorded — ${reclassifying.voucher_number}${reclassifying.bank_reference ? ` — ${reclassifying.bank_reference}` : ''} (duplicate receipt into ${intoLabel} reversed)`
        : `Suspense cleared — internal transfer ${fromLabel} → ${intoLabel} — ${reclassifying.voucher_number}${reclassifying.bank_reference ? ` — ${reclassifying.bank_reference}` : ''}`;
      const je = await postJournal({
        date: new Date().toISOString().slice(0, 10),
        description: desc,
        reference_type: 'SUSPENSE_RECLASSIFY',
        reference_id: reclassifying.id,
        currency_id: reclassifying.currency_id,
        exchange_rate: 1,
        created_by: username,
        branch_id: reclassifyBranchId,
        lines: [
          { account_id: ACCOUNTS.SUSPENSE, debit_amount: amt, credit_amount: 0, description: desc },
          { account_id: creditAccount, debit_amount: 0, credit_amount: amt, description: desc },
          // The receipt was logged against the wrong bank — move it to where it really landed.
          ...(intoChanged ? [
            { account_id: transferIntoId, debit_amount: amt, credit_amount: 0, description: `${desc} — receiving account corrected` },
            { account_id: origInto, debit_amount: 0, credit_amount: amt, description: `${desc} — receiving account corrected` },
          ] : []),
        ],
      });
      await supabase.from('receipt_vouchers').update({
        ...(intoChanged ? { received_into_account_id: transferIntoId } : {}),
        branch_id: reclassifyBranchId,
        suspense_cleared_journal_id: je.id,
        suspense_cleared_at: new Date().toISOString(),
        suspense_cleared_by: username,
      }).eq('id', reclassifying.id);
      setReclassifying(null);
      load();
    } catch (e) {
      setError(e.message || String(e));
    }
    setSaving(false);
  };

  const submitReclassify = async () => {
    if (!clientId) return setError('Pick a client.');
    if (!reclassifyBranchId) return setError('Select which branch this deposit belongs to.');
    if (target === 'invoice' && !selectedInvoiceId) return setError('Pick which invoice this pays.');
    if (target === 'fifo' && fifoLoading) return setError('Still calculating the allocation — please wait a moment and try again.');
    setSaving(true); setError('');
    try {
      const amt = parseFloat(reclassifying.amount);
      const inv = target === 'invoice' ? openInvoices.find(i => i.id === selectedInvoiceId) : null;
      const desc = `Suspense reclassified — ${reclassifying.voucher_number}${inv ? ` — ${inv.invoice_number}` : ''}`;

      let lines;
      if (target === 'fifo') {
        // Same combined-line shape as the Receipt Voucher's own FIFO posting: whatever landed
        // on an opening balance or an invoice reduces AR; anything left over (no more open
        // debt) falls through to Client Deposits as an unapplied advance.
        const arPortion = fifoAllocations.filter(a => a.target_type !== 'advance').reduce((s, a) => s + a.amount, 0);
        const depositPortion = fifoAllocations.filter(a => a.target_type === 'advance').reduce((s, a) => s + a.amount, 0);
        lines = [
          { account_id: ACCOUNTS.SUSPENSE, debit_amount: amt, credit_amount: 0, description: desc, party_type: 'client', party_id: clientId },
          ...(arPortion > 0 ? [{ account_id: ACCOUNTS.AR, debit_amount: 0, credit_amount: arPortion, description: desc, party_type: 'client', party_id: clientId }] : []),
          ...(depositPortion > 0 ? [{ account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: 0, credit_amount: depositPortion, description: desc, party_type: 'client', party_id: clientId }] : []),
        ];
      } else {
        const targetAccount = target === 'invoice' ? ACCOUNTS.AR : ACCOUNTS.CLIENT_DEPOSITS;
        lines = [
          { account_id: ACCOUNTS.SUSPENSE, debit_amount: amt, credit_amount: 0, description: desc, party_type: 'client', party_id: clientId },
          { account_id: targetAccount, debit_amount: 0, credit_amount: amt, description: desc, party_type: 'client', party_id: clientId },
        ];
      }

      const je = await postJournal({
        date: new Date().toISOString().slice(0, 10),
        description: desc,
        reference_type: 'SUSPENSE_RECLASSIFY',
        reference_id: reclassifying.id,
        currency_id: reclassifying.currency_id,
        exchange_rate: 1,
        created_by: username,
        branch_id: reclassifyBranchId,
        lines,
      });

      await supabase.from('receipt_vouchers').update({
        client_id: clientId,
        // The branch is only known now (that's why it sat in Suspense) — record it
        // retroactively on the original voucher too, not just on the new journal entry.
        branch_id: reclassifyBranchId,
        suspense_cleared_journal_id: je.id,
        suspense_cleared_at: new Date().toISOString(),
        suspense_cleared_by: username,
      }).eq('id', reclassifying.id);

      if (target === 'fifo') {
        // Record the allocation trail (old balance -> invoices -> advance) so getInvoicePaidAmount()
        // and any future FIFO run for this client sees this suspense amount as already applied.
        const rows = fifoAllocations.map(a => ({
          receipt_voucher_id: reclassifying.id,
          client_id: clientId,
          invoice_id: a.invoice_id,
          target_type: a.target_type,
          amount: a.amount,
          created_by: username,
        }));
        await supabase.from('receipt_allocations').insert(rows);

        for (const a of fifoAllocations) {
          if (a.target_type !== 'invoice' || !a.invoice_id) continue;
          const { data: invRow } = await supabase.from('invoices').select('line_items').eq('id', a.invoice_id).single();
          const invTotal = (invRow?.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price || li.unitPrice || 0) * parseFloat(li.qty || li.quantity || 1)), 0);
          const totalPaid = await getInvoicePaidAmount(a.invoice_id);
          if (invTotal > 0 && totalPaid >= invTotal - 0.01) {
            await supabase.from('invoices').update({ status: 'PAID' }).eq('id', a.invoice_id);
          }
        }
      } else if (inv) {
        // Same allocation trail for the manual single-invoice path — previously this application
        // was untracked anywhere getInvoicePaidAmount() looks, so a later receipt against this
        // client could double-allocate against an invoice already partly cleared this way.
        await supabase.from('receipt_allocations').insert({
          receipt_voucher_id: reclassifying.id,
          client_id: clientId,
          invoice_id: inv.id,
          target_type: 'invoice',
          amount: amt,
          created_by: username,
        });
        const totalPaid = await getInvoicePaidAmount(inv.id);
        if (inv.total > 0 && totalPaid >= inv.total - 0.01) {
          await supabase.from('invoices').update({ status: 'PAID' }).eq('id', inv.id);
        }
      }

      setReclassifying(null);
      load();
    } catch (err) {
      setError(err.message || 'Failed to reclassify');
    }
    setSaving(false);
  };

  // ---- Split mode: same deposit, several customers' portions ----

  const addSplitRow = () => {
    const allocated = splits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const remaining = Math.max(0, parseFloat(reclassifying.amount) - allocated);
    splitKeySeq.current += 1;
    // Build the row object now, synchronously, capturing this call's own key — NOT inside
    // the setSplits updater below. React only *runs* that updater when it flushes the state
    // update, which can be after several addSplitRow() calls have already each bumped
    // splitKeySeq.current — reading the ref from inside the updater made every row created
    // in the same synchronous burst (e.g. the two seeded when split mode is turned on) land
    // on whatever splitKeySeq.current had reached by flush time, i.e. the SAME key for both,
    // which made React conflate the two rows' state (typing/deleting in one hit the other).
    const newRow = {
      key: splitKeySeq.current, clientId: '', clientLabel: '', amount: remaining ? remaining.toFixed(2) : '',
      target: 'deposit', openInvoices: [], selectedInvoiceId: '', fifoAllocations: [], fifoLoading: false,
    };
    setSplits(prev => [...prev, newRow]);
  };

  const removeSplitRow = (key) => setSplits(prev => prev.filter(r => r.key !== key));

  const patchSplitRow = (key, patch) => setSplits(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const loadSplitFifoPreview = async (key, clientId, amount) => {
    patchSplitRow(key, { fifoLoading: true });
    try {
      const allocs = await computeFifoAllocation(clientId, parseFloat(amount) || 0);
      patchSplitRow(key, { fifoAllocations: allocs, fifoLoading: false });
    } catch {
      patchSplitRow(key, { fifoLoading: false });
    }
  };

  const loadSplitClientInvoices = async (key, id) => {
    const { data } = await supabase.from('invoices')
      .select('id, invoice_number, issued_date, status, line_items')
      .eq('client_id', id).in('status', ['DRAFT', 'SENT']).order('issued_date', { ascending: false }).limit(20);
    const withBalances = (await Promise.all((data || []).map(async inv => {
      const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price || li.unitPrice || 0) * parseFloat(li.qty || li.quantity || 1)), 0);
      const paid = await getInvoicePaidAmount(inv.id);
      return { ...inv, total, paid, balance: total - paid };
    }))).filter(i => i.balance > 0.01);
    patchSplitRow(key, { openInvoices: withBalances });
  };

  const selectSplitClient = (key, c, currentAmount) => {
    patchSplitRow(key, { clientId: c.id, clientLabel: c.full_name, target: 'deposit', selectedInvoiceId: '', fifoAllocations: [] });
    loadSplitClientInvoices(key, c.id);
    loadSplitFifoPreview(key, c.id, currentAmount);
  };

  const setSplitAmount = (key, value) => {
    patchSplitRow(key, { amount: value });
    const row = splits.find(r => r.key === key);
    if (row?.clientId && row.target === 'fifo') loadSplitFifoPreview(key, row.clientId, value);
  };

  const setSplitTarget = (key, t) => {
    patchSplitRow(key, { target: t });
    const row = splits.find(r => r.key === key);
    if (t === 'fifo' && row?.clientId) loadSplitFifoPreview(key, row.clientId, row.amount);
  };

  const splitAllocated = splits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const splitRemaining = reclassifying ? parseFloat(reclassifying.amount) - splitAllocated : 0;
  const splitBalanced = Math.abs(splitRemaining) < 0.01;

  const submitSplitReclassify = async () => {
    if (!reclassifyBranchId) return setError('Select which branch this deposit belongs to.');
    if (splits.length < 2) return setError('Add at least two customers to split this deposit.');
    if (splits.some(r => !r.clientId)) return setError('Pick a client for every split.');
    if (!splitBalanced) return setError(`Split amounts (${fmt(splitAllocated)}) must add up to the full deposit (${fmt(reclassifying.amount)}).`);
    if (splits.some(r => r.target === 'invoice' && !r.selectedInvoiceId)) return setError('Pick which invoice each split pays.');
    if (splits.some(r => r.target === 'fifo' && r.fifoLoading)) return setError('Still calculating an allocation — please wait a moment and try again.');

    setSaving(true); setError('');
    try {
      const amt = parseFloat(reclassifying.amount);
      const desc = `Suspense reclassified (split ${splits.length} ways) — ${reclassifying.voucher_number}`;
      const lines = [{ account_id: ACCOUNTS.SUSPENSE, debit_amount: amt, credit_amount: 0, description: desc }];
      const allocationRows = [];

      splits.forEach(r => {
        const rAmt = parseFloat(r.amount) || 0;
        if (r.target === 'fifo') {
          const arPortion = r.fifoAllocations.filter(a => a.target_type !== 'advance').reduce((s, a) => s + a.amount, 0);
          const depositPortion = r.fifoAllocations.filter(a => a.target_type === 'advance').reduce((s, a) => s + a.amount, 0);
          if (arPortion > 0) lines.push({ account_id: ACCOUNTS.AR, debit_amount: 0, credit_amount: arPortion, description: desc, party_type: 'client', party_id: r.clientId });
          if (depositPortion > 0) lines.push({ account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: 0, credit_amount: depositPortion, description: desc, party_type: 'client', party_id: r.clientId });
          r.fifoAllocations.forEach(a => allocationRows.push({ receipt_voucher_id: reclassifying.id, client_id: r.clientId, invoice_id: a.invoice_id, target_type: a.target_type, amount: a.amount, created_by: username }));
        } else {
          const targetAccount = r.target === 'invoice' ? ACCOUNTS.AR : ACCOUNTS.CLIENT_DEPOSITS;
          lines.push({ account_id: targetAccount, debit_amount: 0, credit_amount: rAmt, description: desc, party_type: 'client', party_id: r.clientId });
          if (r.target === 'invoice') {
            allocationRows.push({ receipt_voucher_id: reclassifying.id, client_id: r.clientId, invoice_id: r.selectedInvoiceId, target_type: 'invoice', amount: rAmt, created_by: username });
          }
        }
      });

      const je = await postJournal({
        date: new Date().toISOString().slice(0, 10),
        description: desc,
        reference_type: 'SUSPENSE_RECLASSIFY',
        reference_id: reclassifying.id,
        currency_id: reclassifying.currency_id,
        exchange_rate: 1,
        created_by: username,
        branch_id: reclassifyBranchId,
        lines,
      });

      await supabase.from('receipt_vouchers').update({
        // No single client owns this deposit anymore — the real breakdown lives in
        // receipt_allocations (one row per client/target below) and in each client's
        // own journal lines, tagged individually via party_id above.
        client_id: null,
        branch_id: reclassifyBranchId,
        suspense_cleared_journal_id: je.id,
        suspense_cleared_at: new Date().toISOString(),
        suspense_cleared_by: username,
        description: `${reclassifying.description} [Split ${splits.length} ways]`,
      }).eq('id', reclassifying.id);

      if (allocationRows.length) await supabase.from('receipt_allocations').insert(allocationRows);

      // Same as the single-client FIFO/invoice paths: flip any invoice to PAID once this
      // split fully clears it.
      const paidOffInvoiceIds = [
        ...splits.filter(r => r.target === 'invoice' && r.selectedInvoiceId).map(r => r.selectedInvoiceId),
        ...splits.filter(r => r.target === 'fifo').flatMap(r => r.fifoAllocations.filter(a => a.target_type === 'invoice' && a.invoice_id).map(a => a.invoice_id)),
      ];
      for (const invId of paidOffInvoiceIds) {
        const { data: invRow } = await supabase.from('invoices').select('line_items').eq('id', invId).single();
        const invTotal = (invRow?.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price || li.unitPrice || 0) * parseFloat(li.qty || li.quantity || 1)), 0);
        const totalPaid = await getInvoicePaidAmount(invId);
        if (invTotal > 0 && totalPaid >= invTotal - 0.01) {
          await supabase.from('invoices').update({ status: 'PAID' }).eq('id', invId);
        }
      }

      setReclassifying(null);
      load();
    } catch (err) {
      setError(err.message || 'Failed to reclassify');
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Suspense Register</div>
          <div className="page-subtitle">Unidentified deposits logged from Receipt Vouchers — clear them out once the depositor is known</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/vouchers/receipt')}>+ New Receipt Voucher</button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
        {[['open', 'Open'], ['cleared', 'Cleared']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: tab === key ? 700 : 400, color: tab === key ? 'var(--primary)' : 'var(--text-muted)', borderBottom: tab === key ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2, fontSize: 14 }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'open' && !loading && rows.length > 0 && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 24, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Open Suspense Balance</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#b45309' }}>LKR {fmt(totalOpen)}</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length} unidentified deposit{rows.length === 1 ? '' : 's'} waiting to be matched to a client</div>
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Voucher</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Deposited Into</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Branch</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Description</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Amount</th>
                {tab === 'open'
                  ? <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Days Pending</th>
                  : <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Cleared</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const days = daysSince(r.voucher_date);
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px' }}>{r.voucher_date}</td>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--primary)' }}>{r.voucher_number}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{r.received_into ? `${r.received_into.account_code} · ${r.received_into.account_name}` : '—'}</td>
                    <td style={{ padding: '10px 16px', color: r.branches ? 'var(--text)' : 'var(--text-muted)', fontStyle: r.branches ? 'normal' : 'italic' }}>{r.branches?.name || 'unknown'}</td>
                    <td style={{ padding: '10px 16px', maxWidth: 320 }}>
                      {r.description}
                      {r.bank_reference && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ref: {r.bank_reference}</div>}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700 }}>{r.currencies?.code} {fmt(r.amount)}</td>
                    {tab === 'open' ? (
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ fontWeight: 600, color: days > 7 ? 'var(--danger)' : days > 2 ? '#b45309' : 'var(--text-muted)' }}>{days}d</span>
                      </td>
                    ) : (
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{r.suspense_cleared_at?.slice(0, 10)} by {r.suspense_cleared_by}</td>
                    )}
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      {tab === 'open' && <button className="btn btn-secondary btn-sm" onClick={() => openReclassify(r)}>Reclassify</button>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{tab === 'open' ? 'No unidentified deposits open right now.' : 'Nothing reclassified yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {reclassifying && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setReclassifying(null); }}>
          <div className="card" style={{ width: splitMode ? 620 : 520, maxHeight: '88vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Reclassify {reclassifying.voucher_number}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              LKR {fmt(reclassifying.amount)} · {reclassifying.description}
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Branch *</label>
              <select className="form-input" value={reclassifyBranchId} onChange={e => setReclassifyBranchId(e.target.value)}>
                <option value="">-- Which branch does this deposit belong to? --</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Not known when this deposit was logged — now that the client is identified, confirm which branch it's for.
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">What was this deposit?</label>
              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="radio" checked={clearMode === 'client'} onChange={() => { setClearMode('client'); setError(''); }} /> A customer payment
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="radio" checked={clearMode === 'transfer'}
                    onChange={() => { setClearMode('transfer'); setSplitMode(false); setSplits([]); setClientLabel(''); setClientId(''); setOpenInvoices([]); setSelectedInvoiceId(''); setFifoAllocations([]); setError(''); }} /> An internal transfer between our own accounts
                </label>
              </div>
            </div>

            {clearMode === 'transfer' && (
              <div style={{ marginBottom: 14 }}>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">Received into *</label>
                  <select className="form-input" value={transferIntoId} onChange={e => { setTransferIntoId(e.target.value); if (e.target.value === transferFromId) setTransferFromId(''); }}>
                    <option value="">-- Which of our accounts did the money land in? --</option>
                    {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                  </select>
                  {reclassifying.received_into_account_id && transferIntoId && transferIntoId !== reclassifying.received_into_account_id && (
                    <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 4 }}>
                      Logged against {reclassifying.received_into ? `${reclassifying.received_into.account_code} ${reclassifying.received_into.account_name}` : 'another account'} — this will also move it to the account selected here.
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">Transferred from {outflowAlreadyRecorded ? '' : '*'}</label>
                  <select className="form-input" value={transferFromId} onChange={e => setTransferFromId(e.target.value)} disabled={outflowAlreadyRecorded}>
                    <option value="">-- Which of our accounts did the money come from? --</option>
                    {bankAccounts.filter(a => a.id !== transferIntoId).map(a => (
                      <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Posts DR Suspense / CR the account above — a plain transfer, no client, AR or revenue.
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer', padding: '8px 12px', background: 'var(--bg)', borderRadius: 6 }}>
                  <input type="checkbox" checked={outflowAlreadyRecorded} onChange={e => { setOutflowAlreadyRecorded(e.target.checked); setError(''); }} style={{ marginTop: 2 }} />
                  <span>The transfer is already recorded elsewhere (e.g. a journal moving it between the two accounts) — this suspense receipt counted the same money twice, so cancel it against the receiving account instead.</span>
                </label>
              </div>
            )}

            {clearMode === 'client' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: '8px 12px', background: 'var(--bg)', borderRadius: 6 }}>
              <input
                type="checkbox"
                checked={splitMode}
                onChange={e => {
                  const on = e.target.checked;
                  setSplitMode(on);
                  setError('');
                  if (on) {
                    setClientLabel(''); setClientId(''); setOpenInvoices([]); setSelectedInvoiceId(''); setFifoAllocations([]);
                    if (splits.length === 0) {
                      // Seed two blank rows directly (not via two addSplitRow() calls) — left
                      // blank rather than guessing a default amount for each, since with no
                      // rows yet there's nothing sensible to split by.
                      splitKeySeq.current += 1; const key1 = splitKeySeq.current;
                      splitKeySeq.current += 1; const key2 = splitKeySeq.current;
                      const blankRow = (key) => ({ key, clientId: '', clientLabel: '', amount: '', target: 'deposit', openInvoices: [], selectedInvoiceId: '', fifoAllocations: [], fifoLoading: false });
                      setSplits([blankRow(key1), blankRow(key2)]);
                    }
                  } else {
                    setSplits([]);
                  }
                }}
              />
              This deposit is actually several customers' payments bundled together — split it
            </label>
            )}

            {clearMode === 'client' && !splitMode && (
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Client *</label>
                <TypeSearch
                  fetchFn={async (q) => {
                    const { data } = await supabase.from('clients').select('id, full_name, client_code').eq('is_active', true)
                      .or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`).order('full_name').limit(30);
                    return data || [];
                  }}
                  value={clientLabel}
                  displayKey="full_name"
                  placeholder="Type client name or code…"
                  onSelect={c => { setClientLabel(c.full_name); setClientId(c.id); setSelectedInvoiceId(''); setTarget('deposit'); loadClientInvoices(c.id); loadFifoPreview(c.id, reclassifying.amount); }}
                  onClear={() => { setClientLabel(''); setClientId(''); setOpenInvoices([]); setSelectedInvoiceId(''); setFifoAllocations([]); }}
                />
              </div>
            )}

            {splitMode && (
              <div style={{ marginBottom: 14 }}>
                {splits.map((r, i) => (
                  <div key={r.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Customer {i + 1}</div>
                      <button type="button" onClick={() => removeSplitRow(r.key)} style={{ border: 'none', background: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Remove</button>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <TypeSearch
                          fetchFn={async (q) => {
                            const { data } = await supabase.from('clients').select('id, full_name, client_code').eq('is_active', true)
                              .or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`).order('full_name').limit(30);
                            return data || [];
                          }}
                          value={r.clientLabel}
                          displayKey="full_name"
                          placeholder="Type client name or code…"
                          onSelect={c => selectSplitClient(r.key, c, r.amount)}
                          onClear={() => patchSplitRow(r.key, { clientId: '', clientLabel: '', openInvoices: [], selectedInvoiceId: '', fifoAllocations: [] })}
                        />
                      </div>
                      <input
                        className="form-input" type="number" step="0.01" style={{ width: 120 }}
                        placeholder="Amount" value={r.amount}
                        onChange={e => setSplitAmount(r.key, e.target.value)}
                      />
                    </div>

                    {r.clientId && (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                            <input type="radio" checked={r.target === 'deposit'} onChange={() => setSplitTarget(r.key, 'deposit')} /> General deposit
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: r.fifoLoading ? 'default' : 'pointer' }}>
                            <input type="radio" checked={r.target === 'fifo'} onChange={() => setSplitTarget(r.key, 'fifo')} disabled={r.fifoLoading} /> FIFO against balance{r.fifoLoading ? ' (checking…)' : ''}
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                            <input type="radio" checked={r.target === 'invoice'} onChange={() => setSplitTarget(r.key, 'invoice')} disabled={r.openInvoices.length === 0} /> Specific invoice{r.openInvoices.length === 0 ? ' (none open)' : ''}
                          </label>
                        </div>

                        {r.target === 'fifo' && r.fifoAllocations.length > 0 && (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                            {r.fifoAllocations.map((a, ai) => (
                              <div key={ai} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', fontSize: 11.5, borderBottom: ai < r.fifoAllocations.length - 1 ? '1px solid var(--border)' : 'none' }}>
                                <span>{a.label}</span>
                                <span style={{ fontWeight: 700 }}>{fmt(a.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {r.target === 'invoice' && (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', maxHeight: 140, overflowY: 'auto' }}>
                            {r.openInvoices.map(inv => (
                              <div key={inv.id} onClick={() => patchSplitRow(r.key, { selectedInvoiceId: inv.id })} style={{
                                display: 'flex', justifyContent: 'space-between', padding: '6px 10px', cursor: 'pointer', fontSize: 11.5,
                                background: r.selectedInvoiceId === inv.id ? 'rgba(34,197,94,0.08)' : 'white',
                                borderBottom: '1px solid var(--border)', borderLeft: r.selectedInvoiceId === inv.id ? '3px solid var(--success)' : '3px solid transparent',
                              }}>
                                <span style={{ fontWeight: 600 }}>{inv.invoice_number}</span>
                                <span style={{ fontWeight: 700, color: 'var(--danger)' }}>Balance: {inv.balance.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                <button type="button" className="btn btn-secondary btn-sm" onClick={addSplitRow} style={{ marginBottom: 10 }}>+ Add Another Customer</button>

                <div style={{ fontSize: 12.5, fontWeight: 600, padding: '8px 12px', borderRadius: 6, background: splitBalanced ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.06)', color: splitBalanced ? 'var(--success)' : 'var(--danger)' }}>
                  Allocated {fmt(splitAllocated)} of {fmt(reclassifying.amount)}
                  {!splitBalanced && ` — ${splitRemaining > 0 ? `${fmt(splitRemaining)} left to assign` : `${fmt(-splitRemaining)} over`}`}
                </div>
              </div>
            )}

            {!splitMode && clientId && (
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Applies To</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" checked={target === 'deposit'} onChange={() => { setTarget('deposit'); setSelectedInvoiceId(''); }} /> General deposit (2010)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: fifoLoading || !hasOutstanding ? 'default' : 'pointer' }}>
                    <input type="radio" checked={target === 'fifo'} onChange={() => setTarget('fifo')} disabled={fifoLoading || !hasOutstanding} /> Pay against balance (FIFO — oldest first){fifoLoading ? ' (checking…)' : !hasOutstanding ? ' (nothing outstanding)' : ''}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" checked={target === 'invoice'} onChange={() => setTarget('invoice')} disabled={openInvoices.length === 0} /> A specific invoice{openInvoices.length === 0 ? ' (none open)' : ''}
                  </label>
                </div>
                {target === 'fifo' && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                    <div style={{ padding: '8px 12px', background: 'rgba(79,110,247,0.06)', fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      This amount will be applied (oldest debt first)
                    </div>
                    {fifoAllocations.map((a, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', fontSize: 12, borderBottom: i < fifoAllocations.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <span>
                          {a.label}
                          {a.target_type === 'old_balance' && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>(opening balance)</span>}
                          {a.target_type === 'advance' && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>(unmatched — advance)</span>}
                        </span>
                        <span style={{ fontWeight: 700 }}>{fmt(a.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {target === 'invoice' && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', maxHeight: 180, overflowY: 'auto' }}>
                    {openInvoices.map(inv => (
                      <div key={inv.id} onClick={() => setSelectedInvoiceId(inv.id)} style={{
                        display: 'flex', justifyContent: 'space-between', padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                        background: selectedInvoiceId === inv.id ? 'rgba(34,197,94,0.08)' : 'white',
                        borderBottom: '1px solid var(--border)', borderLeft: selectedInvoiceId === inv.id ? '3px solid var(--success)' : '3px solid transparent',
                      }}>
                        <span style={{ fontWeight: 600 }}>{inv.invoice_number}</span>
                        <span style={{ fontWeight: 700, color: 'var(--danger)' }}>Balance: {inv.balance.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!splitMode && clientId && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, padding: '8px 12px', background: 'var(--bg)', borderRadius: 6 }}>
                {target === 'fifo'
                  ? `Posts: DR Suspense (2015) / CR ${fifoAllocations.some(a => a.target_type !== 'advance') ? 'Accounts Receivable (1030)' : ''}${fifoAllocations.some(a => a.target_type !== 'advance') && fifoAllocations.some(a => a.target_type === 'advance') ? ' + ' : ''}${fifoAllocations.some(a => a.target_type === 'advance') ? 'Client Deposits (2010)' : ''} — split across ${fifoAllocations.length} item${fifoAllocations.length === 1 ? '' : 's'}, LKR ${fmt(reclassifying.amount)} total`
                  : `Posts: DR Suspense (2015) / CR ${target === 'invoice' ? 'Accounts Receivable (1030)' : 'Client Deposits (2010)'} — LKR ${fmt(reclassifying.amount)}`}
              </div>
            )}

            {splitMode && splits.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, padding: '8px 12px', background: 'var(--bg)', borderRadius: 6 }}>
                Posts: DR Suspense (2015) LKR {fmt(reclassifying.amount)} / CR split across {splits.length} customer{splits.length === 1 ? '' : 's'} (Accounts Receivable and/or Client Deposits per customer's chosen target).
              </div>
            )}

            {error && <div style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 12 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setReclassifying(null)} disabled={saving}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={clearMode === 'transfer' ? submitTransfer : splitMode ? submitSplitReclassify : submitReclassify}
                disabled={
                  saving || !reclassifyBranchId ||
                  (clearMode === 'transfer' ? (!transferIntoId || (!outflowAlreadyRecorded && !transferFromId)) : splitMode
                    ? (splits.length < 2 || splits.some(r => !r.clientId) || !splitBalanced || splits.some(r => r.target === 'invoice' && !r.selectedInvoiceId) || splits.some(r => r.target === 'fifo' && r.fifoLoading))
                    : (!clientId || (target === 'invoice' && !selectedInvoiceId) || (target === 'fifo' && fifoLoading)))
                }
              >
                {saving ? 'Posting…' : clearMode === 'transfer' ? 'Clear as internal transfer' : 'Reclassify'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
